import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios';
import _ from 'lodash';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer } from 'unstated-next';
import * as yup from 'yup';

import { ClassSession, ClassSessionWithGroupData } from '../../components/addSessions/types';
import { useCreateCalendarValues } from '../../components/addSessions/useCreateCalendarValues';
import { aggregateCalendarDates } from '../../components/addSessions/utils/aggregateCalendarDates';
import {
    calcSessionsFromClassTimes,
    calcSessionsFromDates,
    filterSessions
} from '../../components/addSessions/utils/calcSessions';
import { AUTH_ME, CLASS_CREATE, GET_CLASS_DATA_PATH, TEMPLATES_GET, UPDATE_CLASS, USERS } from '../../config/apiPaths';
import { useAppMessaging } from '../../hooks/useAppMessaging';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { ClassData, LmsSyncSettings, PaginatedUsers, UserRecord, ZoomMeetingData } from '../../types/apiTypes';
import { isValidEmail } from '../../utils/inputValidation';
import { PasswordGenerator } from '../../utils/passwordGenerator';
import { getUrlParams, getUrls, processApiPath } from '../../utils/url';
import { usePrevious } from '../../utils/usePrevious';
import { ZoomMeetingSettings } from '../../../../models/types';
import {
    AdditionalInstructors,
    CanJoinBeforeInstructor,
    ClassCreatePayload,
    ClassDataUpdate,
    ClassSessionGroupPayload,
    ClassUpdatePayload,
    InitialClassFormValues,
    MeetingObj,
    RequireAuth,
    TemplateRecord,
    WaitingRoom
} from './types';
import { CLASS_CREATE_INSTRUCTOR_MISSING_ZOOM_CREDENTIALS, CLASS_CREATE_ZOOM_PASSWORD_ERROR } from './error-codes';
import { datesHaveBeenUpdated, groupSessions, returnClassDataDelta, returnMeetingDataDelta } from './classPayloadUtils';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { UnauthorizedUserAccess, WebExpOptions } from '../../types/adminConfig';
import { ClassRecord, ClassSessionGroup, ClassSessionInfo, ClassStatus, ClassStudentGroup } from '../../context/types';
// import ClassesContainer from '../AdminSettings/components/Classes/ClassesContainer';
// import { ClassWithInstructors } from '../AdminSettings/components/Classes/types';

type UpdateClassCallbacks = { onSuccess?: () => void; onError?: () => void };

type LoadingStatus = 'INITIAL' | 'LOADING' | 'LOADING_MORE' | 'SUCCESS' | 'ERROR';

type FormActionType = 'CREATE' | 'EDIT';

interface ClassContainerState {
    onClose?: () => void;
    onUpdate?: () => void;
    classData?: ClassData; // result of the GET_CLASS_DATA_PATH api call
}

const useClassContainer = (state: ClassContainerState = {}) => {
    const { api } = getUrls();

    const { closeWindow, sendMessage } = useAppMessaging();

    const { classUuid: classUuidV1 } = useParams<{ classUuid?: string }>();
    const { class_uuid: classUuidV2 } = getUrlParams();
    const classUuid = classUuidV1 || classUuidV2;
    const [formActionType] = useState<FormActionType>(classUuid ? 'EDIT' : 'CREATE');
    const [initialClassTimes, setInitialClassTimes] = useState<string>('');
    const [classRecord, setClassRecord] = useState<ClassRecord>();
    const [initialData, setInitialData] = useState<InitialClassFormValues>();
    const [meetingSettings, setMeetingSettings] = useState<Partial<ZoomMeetingSettings>>();
    const [ltiState, setLtiState] = useState<any>({});
    const [lmsSyncSettings, setLmsSyncSettings] = useState<LmsSyncSettings>({});
    const [hostEmail, setHostEmail] = useState<string>('');

    const [instructors, setInstructors] = useState<UserRecord[]>([]);
    const [additionalInstructors, setAdditionalInstructors] = useState<UserRecord[]>([]);

    const [instructorsStatus, setInstructorsStatus] = useState<LoadingStatus>('INITIAL');
    const [additionalInstructorsStatus, setAdditionalInstructorsStatus] = useState<LoadingStatus>('INITIAL');

    const [instructorSearchValue, setInstructorSearchValue] = useState('');
    const [additionalInstructorSearchValue, setAdditionalInstructorSearchValue] = useState('');

    const [latestSearchValue, setLatestSearchValue] = useState('');
    const [latestAdditionalSearchValue, setLatestAdditionalSearchValue] = useState('');

    const [debouncedInstructorSearchValue, setDebouncedInstructorSearchValue] = useState<string>('');
    const [debouncedAdditionalInstructorSearchValue, setDebouncedAdditionalInstructorSearchValue] =
        useState<string>('');

    const cancelTokenRef = useRef<CancelTokenSource | null>(null);
    const cancelTokenRefAdditional = useRef<CancelTokenSource | null>(null);

    const [classNameHasBeenSet, setClassNameHasBeenSet] = useState(false);

    const [classAdminSettingsModalOpen, setClassAdminSettingsModalOpen] = useState(false);

    // const classesContainer = useContainer(ClassesContainer);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedInstructorSearchValue(instructorSearchValue);
        }, 500);

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [instructorSearchValue]);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedAdditionalInstructorSearchValue(additionalInstructorSearchValue);
        }, 500);

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [additionalInstructorSearchValue]);

    const [classGroups, setClassGroups] = useState<ClassStudentGroup[]>([]);
    const [originalClassSessionGroups, setOriginalClassSessionGroups] = useState<ClassSessionGroup[]>([]);

    const [createClassStatus, setCreateClassStatus] = useState<LoadingStatus>('INITIAL');
    const [createClassAsDraftStatus, setCreateClassAsDraftStatus] = useState<LoadingStatus>('INITIAL');
    const [updateClassStatus, setUpdateClassStatus] = useState<LoadingStatus>('INITIAL');
    const [getClassToEditStatus, setGetClassToEditStatus] = useState<LoadingStatus>('INITIAL');
    const [publishedTemplates, setPublishedTemplates] = useState<TemplateRecord[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { data: orgSettings } = useOrgSettings();

    const allowForcedAuthentication =
        orgSettings?.zoomAppId !== 'teams' &&
        (orgSettings?.authSettings.forcedAuthentication || meetingSettings?.meeting_authentication);

    const userUuid = window.localStorage.getItem('user_uuid');

    const { t } = useTranslation('create_edit_class');
    const { t: tCommon } = useTranslation('common');

    const {
        schema: calendarSchema,
        defaultValues: defaultCalendarValues,
        defaultValuesWithInitialDay,
        returnNewDefaultCalendarValues
    } = useCreateCalendarValues();

    const schema = yup.object({
        className: yup.string().required(t('details.class-name-required')).max(200, t('details.class-name-max-char')),
        classId: yup.string().optional(),
        description: yup.string().optional().max(2000, t('details.class-description-max-char')),
        classPasscode: yup.string().required(t('details.class-passcode-required')),
        courseTemplate: yup.boolean().optional(),
        courseTemplateId: yup.string().optional(),
        waitingRoom: yup.mixed<WaitingRoom>().oneOf(Object.values(WaitingRoom)),
        canJoinBeforeInstructor: yup.mixed<CanJoinBeforeInstructor>().oneOf(Object.values(CanJoinBeforeInstructor)),
        requireAuth: yup.mixed<RequireAuth>().oneOf(Object.values(RequireAuth)),
        webExperience: yup.mixed<WebExpOptions>().oneOf(Object.values(WebExpOptions)),
        allowVanillaZoom: yup.boolean().optional(),
        unauthorizedUserAccess: yup.mixed<UnauthorizedUserAccess>().oneOf(Object.values(UnauthorizedUserAccess)),
        unauthorizedUserText: yup.string().optional(),
        instructorEmails: yup
            .array()
            .of(yup.string())
            .test({
                test(emails) {
                    if (emails) {
                        const errorEmails: string[] = [];

                        emails.forEach((email) => {
                            if (email && !isValidEmail(email)) {
                                errorEmails.push(email);
                            }
                        });

                        const errorEmailsString = errorEmails.join(', ');

                        if (errorEmails.length) {
                            return this.createError({
                                message: t('advanced.invalid-emails', { errorEmails: errorEmailsString }),
                                path: 'instructorEmails'
                            });
                        }
                    }
                    return true;
                }
            }),
        instructor: yup.string().optional(),
        contentType: yup.string().optional(),
        status: yup.string().oneOf(['published', 'draft']).optional()
    });

    const mergedSchema = schema.concat(yup.object().shape({ dates: yup.array().of(calendarSchema) }));

    const generatePassword = useCallback(() => {
        const passwordGenerator = new PasswordGenerator();
        const password = passwordGenerator.generatePassword({ length: 10 });
        return password;
    }, []);

    const defaultValues = useMemo(
        () => ({
            className: '',
            classId: '',
            description: '',
            classPasscode: generatePassword(),
            courseTemplate: false,
            courseTemplateId: '',
            dates: [defaultValuesWithInitialDay],
            waitingRoom: WaitingRoom.OFF,
            canJoinBeforeInstructor: CanJoinBeforeInstructor.YES,
            requireAuth: RequireAuth.OFF,
            instructorEmails: [] as string[],
            instructor: '',
            contentType: 'default',
            ltiState: {},
            lmsSyncSettings: {},
            classSessions: [] as ClassSessionInfo[],
            classSessionGroups: [] as ClassSessionGroup[],
            classGroups: [] as ClassStudentGroup[],
            classOptions: {},
            webExperience: WebExpOptions.Classic, // updated when orgSettings is available
            allowVanillaZoom: false, // updated when orgSettings is available
            unauthorizedUserAccess: UnauthorizedUserAccess.Allow, // updated when orgSettings is available
            unauthorizedUserText: '', // updated when orgSettings is available
            status: 'published'
        }),
        [defaultValuesWithInitialDay, generatePassword]
    );

    const { control, watch, handleSubmit, register, getValues, reset, formState, setValue } = useForm({
        defaultValues,
        resolver: yupResolver(mergedSchema)
    });

    const formErrors = formState.errors;

    const courseTemplateSelected = watch('courseTemplate');
    useEffect(() => {
        if (!courseTemplateSelected) {
            setValue('courseTemplateId', '');
        }
    }, [setValue, courseTemplateSelected]);

    useEffect(() => {
        // ensures that values will default to whatever the organization has set.
        if (orgSettings?.featureSettings.options.webExperience) {
            setValue('webExperience', orgSettings?.featureSettings.options.webExperience);
        }

        if (orgSettings?.featureSettings.options.allowVanillaZoom) {
            setValue('allowVanillaZoom', orgSettings?.featureSettings.options.allowVanillaZoom);
        }

        if (orgSettings?.authSettings?.unauthorizedUserAccess) {
            setValue('unauthorizedUserAccess', orgSettings?.authSettings?.unauthorizedUserAccess);
        }

        if (orgSettings?.authSettings?.unauthorizedUserText) {
            setValue('unauthorizedUserText', orgSettings?.authSettings?.unauthorizedUserText);
        }
    }, [setValue, orgSettings]);

    const { append, remove, fields } = useFieldArray({ name: 'dates', control });

    const addDefaultDate = () => {
        append(defaultCalendarValues);
    };

    const removeDate = (index: number) => {
        remove(index);
    };

    const watchDatesArray = watch('dates');

    const dateFields = fields.map((field, index) => ({
        ...field,
        ...watchDatesArray[index]
    }));

    const sessions: ClassSessionWithGroupData[] = useMemo(() => {
        // @NOTE: sessions can be derived from user input in the date fields OR
        // sessions can be derived from what came from the database

        if (formState.dirtyFields?.dates || formActionType === 'CREATE') {
            // active representation of user's intent when filling out date fields
            return calcSessionsFromDates(dateFields);
        }

        if (getClassToEditStatus === 'SUCCESS' && initialClassTimes) {
            // representation of what's in the db
            return calcSessionsFromClassTimes(
                initialClassTimes,
                classRecord?.data?.timezone ?? '',
                originalClassSessionGroups
            );
        }

        return [];
    }, [
        formState.dirtyFields?.dates,
        getClassToEditStatus,
        initialClassTimes,
        formActionType,
        dateFields,
        classRecord?.data?.timezone,
        originalClassSessionGroups
    ]);

    const [excludedSessions, setExcludedSessions] = useState<ClassSession[]>([]);

    const prevSessions = usePrevious(sessions);

    useEffect(() => {
        // if sessions are updated, reset excluded sessions
        // this prevents the need for some kind of reset exclusions button, or having to start the form from scratch
        if (!_.isEqual(sessions, prevSessions)) {
            setExcludedSessions([]);
        }
    }, [sessions, prevSessions]);

    const setValuesFromClassData = useCallback(
        (data) => {
            const timezone = data?.class?.data?.timezone ?? '';
            const classTimes = data?.class?.data?.class_times ?? '';
            const dates = aggregateCalendarDates({
                classTimes,
                timezone,
                classSessionGroups: data?.classSessionGroups ?? []
            });

            const classLevelWebExperience = data?.class?.class_options?.webExperience;
            const classLevelAllowVanillaZoom = data?.class?.class_options?.allowVanillaZoom;
            const classLevelUnauthorizedUserAccess = data?.class?.class_options?.unauth_user_access;
            const classLevelUnauthorizedUserText = data?.class?.class_options?.unauth_user_text;

            const orgLevelWebExperience = orgSettings?.featureSettings.options.webExperience;
            const orgLevelAllowVanillaZoom = orgSettings?.featureSettings.options.allowVanillaZoom;
            const orgLevelUnauthorizedUserAccess = orgSettings?.authSettings.unauthorizedUserAccess;
            const orgLevelUnauthorizedUserText = orgSettings?.authSettings.unauthorizedUserText;

            const initial = {
                className: data?.class?.class_name ?? '',
                classId: data?.class?.class_code ?? '',
                description: data?.class.class_description ?? '',
                classPasscode: data?.meeting?.password ?? '',
                waitingRoom: data?.meeting?.settings?.waiting_room ? WaitingRoom.ON : WaitingRoom.OFF,
                // We want canJoinBeforeInstructor to be YES if join_before_host undefined
                canJoinBeforeInstructor:
                    data?.meeting?.settings?.join_before_host === false
                        ? CanJoinBeforeInstructor.NO
                        : CanJoinBeforeInstructor.YES,
                requireAuth: data?.meeting?.settings?.meeting_authentication ? RequireAuth.ON : RequireAuth.OFF,
                dates,
                classSessions: data?.classSessions ?? [],
                classSessionGroups: data?.classSessionGroups ?? [],
                classGroups: data?.classGroups ?? [],
                contentType: data?.class?.content_type ?? 'lms',
                webExperience: classLevelWebExperience ?? orgLevelWebExperience ?? WebExpOptions.Classic,
                allowVanillaZoom: classLevelAllowVanillaZoom ?? orgLevelAllowVanillaZoom ?? true,
                unauthorizedUserAccess:
                    classLevelUnauthorizedUserAccess ?? orgLevelUnauthorizedUserAccess ?? UnauthorizedUserAccess.Allow,
                unauthorizedUserText: classLevelUnauthorizedUserText ?? orgLevelUnauthorizedUserText ?? '',
                classOptions: data?.class?.class_options
            };

            setInitialData(initial);
            reset(initial);
        },
        [orgSettings, reset]
    );

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            const errorCode = error?.response?.data?.code;
            const zoomPasswordError = error?.response?.data?.error;

            if (errorCode === CLASS_CREATE_INSTRUCTOR_MISSING_ZOOM_CREDENTIALS) {
                return t('error.instructor-missing-zoom-credentials');
            }

            if (
                errorCode === CLASS_CREATE_ZOOM_PASSWORD_ERROR &&
                zoomPasswordError &&
                typeof zoomPasswordError === 'string'
            ) {
                return zoomPasswordError;
            }

            return tCommon('errorMessage');
        },
        [t, tCommon]
    );

    const classNameFocused = useRef(false);

    const handleClassNameFocus = () => {
        classNameFocused.current = true;
    };

    const getUserNameFromToken = useCallback(async () => {
        try {
            const { data } = await axios.get(`${api}${AUTH_ME}`, {
                headers: {
                    user_uuid: userUuid as string
                }
            });

            if (classNameFocused.current) return;

            // only set the value if it has not been changed by the user
            if (data?.user_name) {
                setValue('className', t('details.default-class-name', { userName: data.user_name }));
            }
        } catch (err) {
            console.error(err);
        }
    }, [api, setValue, t, userUuid]);

    const getPublishedTemplates = useCallback(async (): Promise<any> => {
        if (!userUuid) return;

        try {
            const config: AxiosRequestConfig = {
                headers: {
                    token: userUuid
                },
                params: { status: ClassStatus.Published }
            };

            const { data } = await axios.get<TemplateRecord[]>(`${api}${TEMPLATES_GET}`, config);

            setPublishedTemplates(data);
        } catch (err) {
            console.error('unable to get published templates');
        }
    }, [api, userUuid]);

    useEffect(() => {
        if (publishedTemplates.length === 0 && formActionType === 'CREATE') {
            getPublishedTemplates();
        }
    }, [formActionType, getPublishedTemplates, publishedTemplates.length]);

    useEffect(() => {
        const classNameIsDirty = formState.dirtyFields.className;

        // classNameIsDirty reverts back to undefined when the entire value is cleared.
        // Must use classNameHasBeenSet to prevent the default class name from being set again

        if (classNameIsDirty || classNameHasBeenSet) return;

        if (formActionType === 'CREATE') {
            setClassNameHasBeenSet(true);

            getUserNameFromToken();
        }
    }, [formActionType, formState.dirtyFields.className, classNameHasBeenSet, getUserNameFromToken]);

    const getUsersFromSearchValue = useCallback(
        async (searchValue, cancelToken): Promise<UserRecord[]> => {
            const options = {
                params: {
                    roles: 'teacher',
                    limit: 500,
                    search: searchValue.toLocaleLowerCase()
                },
                headers: getUserUuidHeaders(userUuid as string),
                cancelToken
            };

            const {
                data: { data }
            } = await axios.get<PaginatedUsers>(`${api}${USERS}`, options);

            const cleanData: UserRecord[] = data.map((user) => ({
                id: user.user_id,
                user_name: user.user_name ?? '',
                email: user.email ?? ''
            }));

            cleanData.sort((a, b) => {
                const aTrimmed = a.user_name.trim();
                const bTrimmed = b.user_name.trim();

                return aTrimmed.localeCompare(bTrimmed);
            });

            return cleanData;
        },
        [api, userUuid]
    );

    const getInstructors = useCallback(async () => {
        // cancel previous request
        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel();
        }

        const source = axios.CancelToken.source();
        cancelTokenRef.current = source;

        if (instructors.length === 0) {
            setInstructorsStatus('LOADING');
        } else {
            setInstructorsStatus('LOADING_MORE');
        }

        try {
            const cleanData = await getUsersFromSearchValue(debouncedInstructorSearchValue, source.token);

            setInstructors(cleanData ?? []);

            setInstructorsStatus('SUCCESS');
        } catch (err) {
            if (axios.isCancel(err)) {
                console.log('Request canceled');
            } else {
                setInstructorsStatus('ERROR');
            }
        }
    }, [instructors.length, getUsersFromSearchValue, debouncedInstructorSearchValue]);

    const getAdditionalInstructors = useCallback(async () => {
        // cancel previous request
        if (cancelTokenRefAdditional.current) {
            cancelTokenRefAdditional.current.cancel();
        }

        const source = axios.CancelToken.source();
        cancelTokenRefAdditional.current = source;

        if (additionalInstructors.length === 0) {
            setAdditionalInstructorsStatus('LOADING');
        } else {
            setAdditionalInstructorsStatus('LOADING_MORE');
        }

        try {
            const cleanData = await getUsersFromSearchValue(debouncedAdditionalInstructorSearchValue, source.token);

            setAdditionalInstructors(cleanData ?? []);

            setAdditionalInstructorsStatus('SUCCESS');
        } catch (err) {
            if (axios.isCancel(err)) {
                console.log('Request canceled');
            } else {
                setAdditionalInstructorsStatus('ERROR');
            }
        }
    }, [additionalInstructors.length, getUsersFromSearchValue, debouncedAdditionalInstructorSearchValue]);

    // for instructors
    useEffect(() => {
        if (debouncedInstructorSearchValue !== latestSearchValue) {
            setLatestSearchValue(debouncedInstructorSearchValue);
            getInstructors();
        }
    }, [debouncedInstructorSearchValue, getInstructors, latestSearchValue]);
    useEffect(() => {
        if (instructors.length === 0) {
            getInstructors();
        }
    }, [debouncedInstructorSearchValue, getInstructors, instructors.length]);

    // for additional instructors
    useEffect(() => {
        if (debouncedAdditionalInstructorSearchValue !== latestAdditionalSearchValue) {
            setLatestAdditionalSearchValue(debouncedAdditionalInstructorSearchValue);
            getAdditionalInstructors();
        }
    }, [debouncedAdditionalInstructorSearchValue, getAdditionalInstructors, latestAdditionalSearchValue]);
    useEffect(() => {
        if (additionalInstructors.length === 0) {
            getAdditionalInstructors();
        }
    }, [debouncedAdditionalInstructorSearchValue, getAdditionalInstructors, additionalInstructors.length]);

    const getClassDataCall = useCallback(async (): Promise<ClassData> => {
        const { data } = await axios.post<ClassData>(
            processApiPath(GET_CLASS_DATA_PATH),
            {
                class_uuid: classUuid,
                with_info: 'true'
            },
            { headers: getUserUuidHeaders(userUuid as string) }
        );

        if ((data as any)?.success === false) {
            throw Error((data as any)?.error);
        }
        if (data && data.classSessions && data.classSessionGroups) {
            const classSessionGroupsByUuid = {};
            data.classSessionGroups.forEach((classSessionGroup) => {
                classSessionGroupsByUuid[classSessionGroup.class_session_group_uuid] = classSessionGroup;
            });
            data.classSessions.forEach((classSession) => {
                if (classSession.class_session_group_uuid) {
                    classSession.class_session_group = classSessionGroupsByUuid[classSession.class_session_group_uuid];
                }
            });
        }
        return data;
    }, [userUuid, classUuid]);

    const getClass = useCallback(async () => {
        setGetClassToEditStatus('LOADING');

        try {
            const data = state.classData ?? (await getClassDataCall());
            const zoomMeetingData = data?.meeting as ZoomMeetingData;

            setInitialClassTimes(data?.class?.data?.class_times ?? '');
            setValuesFromClassData(data);
            setClassRecord(data.class);
            setMeetingSettings(zoomMeetingData?.settings);
            setHostEmail(zoomMeetingData?.host_email || '');

            setClassGroups(data.classGroups || []);
            setOriginalClassSessionGroups(data.classSessionGroups || []);

            setLtiState(data?.class?.lti_state || {});
            setLmsSyncSettings(data?.class?.lms_sync_settings || {});

            setGetClassToEditStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetClassToEditStatus('ERROR');
        }
    }, [state.classData, getClassDataCall, setValuesFromClassData, returnErrorMessage]);

    useEffect(() => {
        // Edit existing class
        if (classUuid && userUuid) {
            getClass();
        }
    }, [classUuid, getClass, userUuid]);

    const excludeSession = (session: ClassSession) => {
        setExcludedSessions((previous) => [...previous, session]);
    };

    const returnCreateClassPayload = useCallback(() => {
        const {
            className,
            classId,
            description,
            dates,
            classPasscode,
            canJoinBeforeInstructor,
            waitingRoom,
            requireAuth,
            courseTemplate,
            courseTemplateId,
            instructorEmails,
            contentType,
            instructor,
            status,
            webExperience,
            allowVanillaZoom,
            unauthorizedUserAccess,
            unauthorizedUserText
        } = getValues();
        const filteredSessions = filterSessions({ sessions, excludedSessions });
        const startAndEnd = filteredSessions.map((session) => `${session.start_time}:${session.end_time}`);

        const instructorsPayload: AdditionalInstructors[] = [];
        if (instructorEmails) {
            instructorEmails.forEach((instructorEmail) => {
                const found = instructors.find((_instructor) => _instructor.email === instructorEmail);

                if (found) {
                    instructorsPayload.push({ email: found.email, user_name: found.user_name });
                } else {
                    instructorsPayload.push({ email: instructorEmail, user_name: instructorEmail });
                }
            });
        }

        const meetingObj: MeetingObj = {
            topic: className,
            password: classPasscode,
            agenda: description,
            settings: {
                join_before_host: canJoinBeforeInstructor === CanJoinBeforeInstructor.YES,
                waiting_room: waitingRoom === WaitingRoom.ON,
                meeting_authentication: requireAuth === RequireAuth.ON
            }
        };

        const classSessionGroupsPayloadArray: ClassSessionGroupPayload[] = groupSessions(sessions);

        const classDataNew: ClassDataUpdate = {
            class_name: className,
            class_description: description,
            class_code: classId,
            content_type: contentType
        };

        if (courseTemplate && courseTemplateId) {
            classDataNew.template_id = courseTemplateId;
        }

        if (dates.length) {
            classDataNew.timezone = dates[0]?.timezone ?? null;
            classDataNew.class_times = startAndEnd.join(',');
        }

        if (dates.length) {
            classDataNew.timezone = dates[0]?.timezone ?? null;
            classDataNew.class_times = startAndEnd.join(',');
        }

        const payload: ClassCreatePayload = {
            meeting_obj: meetingObj,
            class_data: classDataNew,
            user_uuid: userUuid,
            class_session_groups: classSessionGroupsPayloadArray,
            additional_instructors: instructorsPayload,
            status,
            class_options: {
                webExperience: webExperience ?? WebExpOptions.Classic,
                unauth_user_access: unauthorizedUserAccess ?? UnauthorizedUserAccess.Allow,
                unauth_user_text: unauthorizedUserText ?? null,
                allowVanillaZoom: allowVanillaZoom ?? false
            }
        };

        if (instructor) {
            payload.instructor = instructor;
        }

        return payload;
    }, [excludedSessions, getValues, instructors, sessions, userUuid]);

    const returnEditClassPayload = useCallback(() => {
        const formValues = getValues();

        const initialSessions = calcSessionsFromClassTimes(
            initialClassTimes,
            classRecord?.data?.timezone ?? '',
            originalClassSessionGroups
        );
        const initialStartAndEnd = initialSessions.map((session) => `${session.start_time}:${session.end_time}`);

        const filteredSessions = filterSessions({ sessions, excludedSessions });
        const startAndEnd = filteredSessions.map((session) => `${session.start_time}:${session.end_time}`);

        const meetingDelta = returnMeetingDataDelta(initialData, formValues);

        const initialFormData: any = initialData;
        const formData: any = formValues;

        if (initialData && initialData.dates.length) {
            initialFormData.timezone = initialData.dates[0]?.timezone ?? null;
            initialFormData.class_times = initialStartAndEnd.join(',');
        }

        if (formValues.dates.length) {
            formData.timezone = formValues.dates[0]?.timezone ?? null;
            formData.class_times = startAndEnd.join(',');
        }

        const classDataDelta = returnClassDataDelta(initialFormData, formData);

        const payload: ClassUpdatePayload = {
            user_uuid: userUuid,
            class_uuid: classUuid /* using classUuid rather than meetingId which doesn't ever change */
        };

        if (!_.isEmpty(meetingDelta)) {
            payload.meeting_obj = meetingDelta;
        }

        if (!_.isEmpty(classDataDelta)) {
            payload.class_data = classDataDelta;
        }

        const datesHaveUpdates = datesHaveBeenUpdated(formState.dirtyFields);

        if (datesHaveUpdates) {
            payload.class_session_groups = groupSessions(sessions);
        }

        return payload;
    }, [
        classRecord?.data?.timezone,
        classUuid,
        excludedSessions,
        formState.dirtyFields,
        getValues,
        initialClassTimes,
        initialData,
        originalClassSessionGroups,
        sessions,
        userUuid
    ]);

    const onClose = useCallback(() => {
        state.onClose?.();
        closeWindow();
    }, [closeWindow, state]);

    const resetDefaultValues = useCallback(() => {
        // Ensures that new classes will have a new passcode, and updated calendar values
        const { defaultValuesWithInitialDay: defaultDates } = returnNewDefaultCalendarValues();

        const newDefaultValues = {
            ...defaultValues,
            classPasscode: generatePassword(),
            dates: [defaultDates]
        };

        reset(newDefaultValues);
    }, [defaultValues, generatePassword, reset, returnNewDefaultCalendarValues]);

    const handleSuccess = useCallback(() => {
        sendMessage({
            func: 'call_js_on_labeled_window',
            data: { func: 'refresh', label: 'login_window' }
        });

        sendMessage({ func: 'refresh_parent_window' });

        state.onUpdate?.();
        onClose();
        resetDefaultValues();

        setClassAdminSettingsModalOpen(false);
    }, [sendMessage, state, onClose, resetDefaultValues]);

    const onCancel = () => {
        setErrorMessage('');
        setCreateClassStatus('INITIAL');
        onClose();
    };

    const createClass = async ({ payload, onSuccess, onError }) => {
        try {
            await axios.post(`${api}${CLASS_CREATE}`, payload);
            onSuccess();
            handleSuccess();
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            onError();
        }
    };

    const addClassToState = () => {
        // todo: add the new class to the classesContainer
        // const newClass = {} as ClassWithInstructors;
        // const updatedClasses = [newClass, ...classesContainer.classes];
        // will need the actual class uuid back
        // classesContainer.setClasses(updatedClasses);
    };

    const createPublishedClass = async () => {
        setCreateClassStatus('LOADING');

        const payload = returnCreateClassPayload();

        await createClass({
            payload,
            onSuccess: () => {
                addClassToState();
                setCreateClassStatus('SUCCESS');
            },
            onError: () => setCreateClassStatus('ERROR')
        });
    };

    const createClassAsDraft = async () => {
        setCreateClassAsDraftStatus('LOADING');
        setValue('status', 'draft');
        const payload = returnCreateClassPayload();

        await createClass({
            payload,
            onSuccess: () => {
                addClassToState();
                setCreateClassAsDraftStatus('SUCCESS');
            },
            onError: () => setCreateClassAsDraftStatus('ERROR')
        });
    };

    const updateClass = useCallback(
        async ({ onSuccess, onError }: UpdateClassCallbacks = {}) => {
            setUpdateClassStatus('LOADING');

            try {
                const payload = returnEditClassPayload();

                await axios.put(`${api}${UPDATE_CLASS}`, { ...payload });

                setUpdateClassStatus('SUCCESS');

                if (onSuccess) {
                    onSuccess();
                } else {
                    handleSuccess();
                }
            } catch (err) {
                const errorMsg = returnErrorMessage(err);
                setErrorMessage(errorMsg);
                setUpdateClassStatus('ERROR');
                if (onError) {
                    onError();
                }
            }
        },
        [api, handleSuccess, returnEditClassPayload, returnErrorMessage]
    );

    return {
        allowForcedAuthentication,

        excludedSessions,
        excludeSession,

        handleSubmit,
        formActionType,
        register,
        addDefaultDate,
        createPublishedClass,
        createClassAsDraft,
        createClassStatus,
        createClassAsDraftStatus,
        handleClassNameFocus,

        updateClass,
        updateClassStatus,
        classRecord,
        fetchClassData: getClass,

        classGroups,

        publishedTemplates,

        instructorsStatus,
        instructors,

        additionalInstructorsStatus,
        additionalInstructors,

        instructorSearchValue,
        setInstructorSearchValue,

        additionalInstructorSearchValue,
        setAdditionalInstructorSearchValue,

        hostEmail,
        setHostEmail,

        getClassToEditStatus,
        removeDate,
        sessions,
        control,
        reset,
        errorMessage,
        onCancel,
        dateFields,
        setValue,
        watch,

        ltiState,
        lmsSyncSettings,
        formErrors,
        userUuid,

        resetDefaultValues,
        classAdminSettingsModalOpen,
        setClassAdminSettingsModalOpen,
        zoomAppId: orgSettings?.zoomAppId,
        schoolOptions: orgSettings?.featureSettings?.options
    };
};

export const ClassContainer = createContainer(useClassContainer);
